import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ageGroupsAPI, matchesAPI, membershipFeesAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { AgeGroupSelect } from '../../types/ageGroup';
import type { SelectablePlayer } from '../../types/match';
import type { FeeGenerationResult, GenerateFeesRequest } from '../../types/membershipFee';
import PlayerPicker from '../players/PlayerPicker';

interface GenerateFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** A lapon éppen nézett hónap - ezzel nyílik. */
  year: number;
  month: number;
  defaultAmount: number;
  /** Sikeres generálás után (a lap a generált hónapra ugorhat). */
  onGenerated: (year: number, month: number) => void;
}

type Scope = 'club' | 'ageGroups' | 'players';

const MONTH_NAMES = ['Január', 'Február', 'Március', 'Április', 'Május', 'Június', 'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'];
const formatAmount = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Tagdíjak generálása lépésenként: melyik hónapra, kinek (egész klub,
 * korosztályok vagy kiválasztott játékosok), mekkora összeggel - és
 * előnézet arról, kinek, mennyi készül. 0 Ft-os tagdíj nem jön létre.
 */
const GenerateFeesModal: React.FC<GenerateFeesModalProps> = ({ isOpen, onClose, year, month, defaultAmount, onGenerated }) => {
  const { showToast } = useToast();
  const [targetYear, setTargetYear] = useState(year);
  const [targetMonth, setTargetMonth] = useState(month);
  const [scope, setScope] = useState<Scope>('club');
  const [ageGroups, setAgeGroups] = useState<AgeGroupSelect[]>([]);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<number[]>([]);
  const [players, setPlayers] = useState<SelectablePlayer[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [preview, setPreview] = useState<FeeGenerationResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showLines, setShowLines] = useState(false);

  // Megnyitáskor a lapon nézett hónappal és az egész klubbal indulunk
  useEffect(() => {
    if (!isOpen) return;
    setTargetYear(year);
    setTargetMonth(month);
    setScope('club');
    setSelectedAgeGroups([]);
    setSelectedPlayers([]);
    setUseCustomAmount(defaultAmount <= 0);
    setCustomAmount('');
    setPreview(null);
    setShowLines(false);
    ageGroupsAPI.getForSelect().then(setAgeGroups).catch(() => setAgeGroups([]));
    matchesAPI.getSelectablePlayers().then(setPlayers).catch(() => setPlayers([]));
  }, [isOpen, year, month, defaultAmount]);

  const amountValue = useCustomAmount ? Number(customAmount) : null;
  const amountInvalid = useCustomAmount && (!customAmount || !Number.isFinite(amountValue) || (amountValue ?? 0) < 1);
  const scopeEmpty =
    (scope === 'ageGroups' && selectedAgeGroups.length === 0) || (scope === 'players' && selectedPlayers.length === 0);

  const request = useMemo<GenerateFeesRequest>(
    () => ({
      year: targetYear,
      month: targetMonth,
      ageGroupIds: scope === 'ageGroups' ? selectedAgeGroups : undefined,
      playerIds: scope === 'players' ? selectedPlayers : undefined,
      amount: useCustomAmount ? amountValue : null,
    }),
    [targetYear, targetMonth, scope, selectedAgeGroups, selectedPlayers, useCustomAmount, amountValue]
  );

  // Az előnézet minden változásra frissül (kis késleltetéssel, gépelés közben ne kérdezzen)
  useEffect(() => {
    if (!isOpen || amountInvalid || scopeEmpty) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await membershipFeesAPI.previewGeneration(request);
        if (!cancelled) setPreview(result);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          showToast(getErrorMessage(err, 'Az előnézet betöltése nem sikerült.'), 'error');
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, request, amountInvalid, scopeEmpty, showToast]);

  const generate = async () => {
    setGenerating(true);
    try {
      const result = await membershipFeesAPI.runGeneration(request);
      showToast(
        result.createdCount > 0
          ? `${result.createdCount} tagdíj elkészült, összesen ${formatAmount(result.totalAmount)}.`
          : 'Nem készült új tagdíj.'
      );
      onGenerated(targetYear, targetMonth);
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, 'A generálás nem sikerült.'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const toggleAgeGroup = (id: number) =>
    setSelectedAgeGroups((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const years = [year - 1, year, year + 1];
  const canGenerate = !!preview && preview.lines.length > 0 && !previewing && !generating;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[90vh] md:max-w-2xl md:rounded-lg">
          <div className="flex shrink-0 items-center justify-between border-b p-4 md:px-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Tagdíjak generálása</Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto p-4 pb-safe-6 md:px-6">
            {/* 1. Melyik hónap */}
            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">1. Melyik hónapra?</h4>
              <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
                <select
                  value={targetYear}
                  onChange={(e) => setTargetYear(Number(e.target.value))}
                  aria-label="Év"
                  className={inputClass}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <select
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(Number(e.target.value))}
                  aria-label="Hónap"
                  className={inputClass}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* 2. Kinek */}
            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">2. Kinek?</h4>
              <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
                {([
                  ['club', 'Egész klub'],
                  ['ageGroups', 'Korosztályok'],
                  ['players', 'Kiválasztott játékosok'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setScope(value)}
                    aria-pressed={scope === value}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      scope === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {scope === 'club' && (
                <p className="mt-2 text-sm text-gray-500">Minden aktív játékos kap tagdíjat, akinek erre a hónapra még nincs.</p>
              )}
              {scope === 'ageGroups' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {ageGroups.map((g) => {
                    const active = selectedAgeGroups.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => toggleAgeGroup(g.id)}
                        aria-pressed={active}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                          active ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              )}
              {scope === 'players' && (
                <div className="mt-3">
                  <PlayerPicker
                    players={players}
                    selectedIds={selectedPlayers}
                    onChange={setSelectedPlayers}
                    placeholder="Keress játékosra..."
                  />
                </div>
              )}
            </section>

            {/* 3. Mennyi */}
            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">3. Mekkora összeggel?</h4>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="fee-amount"
                    checked={!useCustomAmount}
                    onChange={() => setUseCustomAmount(false)}
                    disabled={defaultAmount <= 0}
                    className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    Az alapértelmezett havi díj ({formatAmount(defaultAmount)})
                    <span className="block text-xs text-gray-500">Akinek egyedi díja van, az azt kapja.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="fee-amount"
                    checked={useCustomAmount}
                    onChange={() => setUseCustomAmount(true)}
                    className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Mindenkinek ugyanannyi erre a hónapra</span>
                </label>
                {useCustomAmount && (
                  <div className="ml-6 flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      aria-label="Összeg forintban"
                      placeholder="Pl. 8000"
                      className={`${inputClass} w-40`}
                    />
                    <span className="text-sm text-gray-600">Ft</span>
                  </div>
                )}
                {defaultAmount <= 0 && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-700">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                    Nincs beállítva alapértelmezett havi díj (Beállítások). Adj meg összeget, vagy állítsd be előbb.
                  </p>
                )}
              </div>
            </section>

            {/* Előnézet */}
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-2 text-sm font-semibold text-gray-900">Előnézet</h4>
              {scopeEmpty ? (
                <p className="text-sm text-gray-500">
                  {scope === 'ageGroups' ? 'Válassz legalább egy korosztályt.' : 'Válassz legalább egy játékost.'}
                </p>
              ) : amountInvalid ? (
                <p className="text-sm text-gray-500">Adj meg legalább 1 Ft-os összeget.</p>
              ) : !preview ? (
                <p className="text-sm text-gray-500">Számolás...</p>
              ) : (
                <div className={`space-y-2 text-sm ${previewing ? 'opacity-60' : ''}`}>
                  <p className="text-gray-900">
                    <span className="text-2xl font-bold">{preview.lines.length}</span> tagdíj készül,{' '}
                    összesen <span className="font-semibold">{formatAmount(preview.totalAmount)}</span> –{' '}
                    {MONTH_NAMES[targetMonth - 1].toLowerCase()} {targetYear}.
                  </p>
                  {preview.alreadyExistsCount > 0 && (
                    <p className="text-gray-600">
                      {preview.alreadyExistsCount} játékosnak erre a hónapra már van tagdíja – ők kimaradnak.
                    </p>
                  )}
                  {preview.zeroAmountPlayers.length > 0 && (
                    <p className="text-amber-700">
                      0 Ft jönne ki, ezért kimarad ({preview.zeroAmountPlayers.length}):{' '}
                      {preview.zeroAmountPlayers.slice(0, 8).join(', ')}
                      {preview.zeroAmountPlayers.length > 8 && ` és még ${preview.zeroAmountPlayers.length - 8}`}
                    </p>
                  )}
                  {preview.lines.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowLines((v) => !v)}
                        className="text-sm font-medium text-primary-700 hover:underline"
                      >
                        {showLines ? 'Lista elrejtése' : 'Kik kapnak tagdíjat?'}
                      </button>
                      {showLines && (
                        <ul className="max-h-56 divide-y divide-gray-200 overflow-y-auto rounded-md bg-white ring-1 ring-gray-200">
                          {preview.lines.map((l) => (
                            <li key={l.playerId} className="flex items-center justify-between gap-2 px-3 py-1.5">
                              <span className="min-w-0 truncate">
                                {l.playerName}
                                {l.ageGroup && <span className="ml-1.5 text-xs text-gray-500">{l.ageGroup}</span>}
                              </span>
                              <span className="shrink-0 tabular-nums text-gray-700">{formatAmount(l.amount)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 md:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {generating
                ? 'Generálás...'
                : preview && preview.lines.length > 0
                  ? `${preview.lines.length} tagdíj generálása`
                  : 'Generálás'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default GenerateFeesModal;
